import { Injectable } from '@angular/core';

@Injectable({
  providedIn: 'root',
})
export class TaxSuiteAntivirusService {
  private readonly allowedExtensions: string[] = [
    'pdf',
    'jpg',
    'jpeg',
    'jfif',
    'png',
    'svg',
    'gif',
    'doc',
    'docx',
    'xls',
    'xlsx',
    'xlsm',
    'ppt',
    'pptx',
    'msg',
    'txt',
    'blob',
    'csv',
    'xml',
    'rtf',
    'bmp',
    'tiff',
    'mp3',
    'mp4',
    'avi',
    'mov',
    'mkv',
    'zip', // REVISAR ACA
    // ACR MODELS BELOW
    '05',
    '06',
    '030',
    '036',
    '038 ',
    '039',
    '100',
    '108',
    '111',
    '113',,
    '115',
    '117',
    '122',
    '123',
    '124',
    '126',
    '128',
    '130',
    '131',
    '141',
    '145',
    '149',
    '151',
    '156',
    '159',
    '165',
    '170',
    '171',
    '180',
    '181',
    '182',
    '184',
    '187',
    '188',
    '189',
    '190',
    '192',
    '193',
    '194',
    '195',
    '196',
    '198',
    '199',
    '200',
    '202',
    '208',
    '210',
    '211',
    '213',
    '216',
    '217',
    '218',
    '220',
    '222',
    '225',
    '226',
    '232',
    '237',
    '270',
    '280',
    '281',
    '282',
    '283',
    '291',
    '294',
    '295',
    '296',
    '299',
    '303',
    '308',
    '309',
    '318',
    '320',
    '322',
    '330',
    '332',
    '340',
    '341',
    '345',
    '346',
    '347',
    '349',
    '410',
    '411',
    '430',
    '480',
    '490',
    '500',
    '568',
    '576',
    '604',
    '611',
    '616',
    '681',
    '682',
    '683',
    '684',
    '685',
    '695',
    '696',
    '714',
    '718',
    '720',
    '763',
    '781',
    '792',
    '793',
    '795',
    '796',
    '797',
    '798',
    '840',
    '952',
    '995',
    '996',
    '997'
  ];
  private readonly magicNumbers: { [key: string]: string[] } = {
    '25-50-44-46': ['pdf'],
    'FF-D8-FF-E1': ['jpg', 'jpeg'],
    'FF-D8-FF-E0': ['jpg', 'jpeg', 'jfif'],
    '89-50-4E-47': ['png'],
    '47-49-46-38': ['gif'],
    'D0-CF-11-E0': ['doc', 'xls', 'ppt', 'msg'],
    '50-4B-03-04': ['docx', 'xlsx', 'pptx', 'blob', 'xlsm', 'zip'],
    '7B-5C-72-74': ['rtf'],
    '42-4D': ['bmp'],
    '49-49-2A-00': ['tiff'],
    '4D-4D-00-2A': ['tiff'],
    '49-44-33': ['mp3'],
    'FF-FB': ['mp3'],
    '00-00-00-18-66-74-79-70-6D-70-34-32': ['mp4'],
    '00-00-00-14-66-74-79-70-69-73-6F-6D': ['mp4'],
    '52-49-46-46': ['avi'],
    '00-00-00-14-66-74-79-70-71-74-20-20': ['mov'],
    '1A-45-DF-A3': ['mkv'],
  };
  private readonly blockedExtensions: string[] = [
    'exe',
    'bat',
    'sh',
    'js',
    'vbs',
    'cmd',
    'com',
    'dll',
    'msi',
    // 'zip', //REVISAR PARA QUE PASE SOLO EL QUE VIENE DEL BLOB
    'rar',
    '7z',
    'tar',
    'gz',
    'scr',
    'ps1',
    'jar',
    'py',
    'rb',
  ];
  private readonly malwareMagicNumbers: { [key: string]: string[] } = {
    '4D-5A-90-00': ['exe', 'dll', 'com', 'msi'],
    '50-4B-03-04': ['docx', 'xlsx', 'pptx', 'xlsm', 'zip'],
    '52-61-72-21': ['rar'],
    '37-7A-BC-AF': ['7z'],
    '1F-8B-08': ['gz'],
    '75-73-74-61-72': ['tar'],
    'D0-CF-11-E0': ['doc', 'xls', 'ppt', 'msg'],
    '': ['txt'],
  };
  private readonly MAX_FILE_SIZE: number = 500 * 1024 * 1024; // 500MB

  async execute(
    file: File
  ): Promise<{ isValid: boolean; validationMessages: string[] }> {
    const isValidFileExtension = this.validateFileExtension(file);
    const isValidFileSize = this.validateFileSize(file);
    const isValidFileContent = await this.validateFileContent(
      file,
      this.getFileExtension(file)
    );

    const isValid =
      isValidFileExtension && isValidFileSize && isValidFileContent;
    const validationMessages = this.getValidationMessages(
      isValidFileExtension,
      isValidFileSize,
      isValidFileContent
    );

    return { isValid, validationMessages };
  }

  async executeMultiple(
    files: File[]
  ): Promise<{ isValid: boolean; validationMessages: string[] }> {
    for (const file of files) {
      const result = await this.execute(file);
      if (!result.isValid) {
        return result;
      }
    }
    return { isValid: true, validationMessages: [] };
  }

  private validateFileExtension(file: File): boolean {
    const fileName = file.name.trim().toLowerCase();
    if (!fileName) return false;

    const splittedContent = fileName.split('.');

    if (splittedContent[0] === 'blob') return true;
    // LINEA ANTIGUA: if (!splittedContent || fileName.includes('..')) return false;
    if (!splittedContent) return false;


    const ext = splittedContent[splittedContent.length - 1];
    if (this.blockedExtensions.includes(ext)) return false;

    return this.allowedExtensions.includes(ext);
  }

  private validateFileSize(file: File): boolean {
    return file.size <= this.MAX_FILE_SIZE;
  }

  private async validateFileContent(file: File, ext: string): Promise<boolean> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (event) => {
        const arrayBuffer = event.target?.result as ArrayBuffer;
        const bytes = new Uint8Array(arrayBuffer).subarray(0, 16); // Check first 16 bytes
        const hex = Array.from(bytes)
          .map((b) => b.toString(16).padStart(2, '0'))
          .join('-')
          .toUpperCase();

        // Special handling for SVG
        if (ext === 'svg') {
          const textDecoder = new TextDecoder();
          const content = textDecoder.decode(arrayBuffer).trim();
          if (content.startsWith('<?xml') && content.includes('<svg')) {
            resolve(true);
          } else {
            console.error('Invalid SVG file content.');
            resolve(false);
          }
          return;
        }

        // Special handling for CSV
        if (ext === 'csv') {
          const textDecoder = new TextDecoder();
          const content = textDecoder.decode(arrayBuffer).trim();
          const lines = content.split(/\r?\n/);
          if (lines.length >= 1 && (lines[0].includes(',') || lines[0].includes(';')) || lines[0] == '') {
            resolve(true);
          } else if (lines.length == 0) {
            resolve(true);
          } else {
            console.error('Invalid CSV file content.');
            resolve(false);
          }
          return;
        }

        // XML files do not have a stable magic number and must be validated as text markup.
        if (ext === 'xml') {
          const textDecoder = new TextDecoder();
          const content = textDecoder.decode(arrayBuffer).trim();
          const normalizedContent = content.replace(/^\uFEFF/, '').trim();
          if (normalizedContent.startsWith('<?xml') || normalizedContent.startsWith('<')) {
            resolve(true);
          } else {
            console.error('Invalid XML file content.');
            resolve(false);
          }
          return;
        }

        // Check against magic numbers of varying lengths
        const magicKey = Object.keys(this.magicNumbers).find((key) =>
          hex.startsWith(key)
        );
        if (magicKey) {
          const allowedExtensionsForMagic = this.magicNumbers[magicKey];
          if (this.isBlockedExtension(allowedExtensionsForMagic)) {
            console.error(`Blocked file extension detected: ${ext}`);
            resolve(false);
            return;
          }
          if (
            !allowedExtensionsForMagic.includes(ext) &&
            !['txt', 'blob', 'zip'].includes(ext)
          ) {
            console.error(
              `File extension mismatch: Expected ${allowedExtensionsForMagic.join(
                ', '
              )}, got ${ext}`
            );
            resolve(false);
            return;
          }
        }

        const malwareMagicKey = Object.keys(this.malwareMagicNumbers).find(
          (key) => hex.startsWith(key)
        );
        if (malwareMagicKey) {
          const allowedExtensionsForMalwareMagic =
            this.malwareMagicNumbers[malwareMagicKey];
          if (
            this.isBlockedExtension(allowedExtensionsForMalwareMagic) &&
            !this.isAllowedExtension(allowedExtensionsForMalwareMagic)
          ) {
            console.error(`Blocked file detected: ${ext}`);
            resolve(false);
            return;
          }
          if (
            !allowedExtensionsForMalwareMagic.includes(ext) &&
            !['txt', 'blob'].includes(ext)
          ) {
            console.error(
              `File extension mismatch: Expected ${allowedExtensionsForMalwareMagic.join(
                ', '
              )}, got ${ext}`
            );
            resolve(false);
            return;
          }
        }

        resolve(true);
      };
      reader.onerror = () => reject(false);
      reader.readAsArrayBuffer(file.slice(0, 16));
    });
  }

  private isBlockedExtension(extensions: string[]): boolean {
    return this.blockedExtensions.some((ext) => extensions.includes(ext));
  }

  private isAllowedExtension(extensions: string[]): boolean {
    return this.allowedExtensions.some((ext) => extensions.includes(ext));
  }

  private getFileExtension(file: File): string {
    return file.name.split('.').pop()?.toLowerCase() || '';
  }

  private getValidationMessages(
    isValidFileExtension: boolean,
    isValidFileSize: boolean,
    isValidFileContent: boolean
  ): string[] {
    const messages: string[] = [];
    if (!isValidFileExtension) {
      messages.push(
        `Allowed file extensions: ${this.allowedExtensions.join(', ')}`
      );
    }
    if (!isValidFileSize) {
      messages.push(`File size must be less than ${this.MAX_FILE_SIZE} bytes.`);
    }
    if (!isValidFileContent) {
      messages.push('Invalid file content.');
    }
    return messages;
  }
}
